// Estimator priced notes, recognised by what the text says (CN1, Peter
// 2026-10-13: "file names shouldnt dictate anything. only the contents.").
//
// Before CN1 the business's own priced working notes were recognised only by
// a file name ("quote notes.txt"), so the same notes saved as "Notes.txt" or
// "site notes.txt" sent the estimator's prices into every prompt. This module
// reads the text alone. A file name is never an input.
//
// The notes this recognises: short, terse lines of operations, quantities and
// the estimator's own sums ("remove tiles 18sqm $720", "labour 2men 3days
// $2,400", "Total price = $2,750 incl gst", "allow $640 for membrane"). It does
// NOT fire on:
//   - a client quote or a competitor's quote (a formal quote layout, a
//     business identity, a letterhead, or words addressed to a reader);
//   - an RFQ (it asks for a price), including one that states a budget;
//   - a site inspection report, which mentions a figure in passing ("owner
//     advised the last repair cost $2,500") or carries report structure;
//   - a supplier or tax invoice.
//
// Pure and synchronous. Text over ESTIMATOR_NOTES_MAX_CHARS is never notes,
// so a prompt that asks about every document reads at most that much of each.

/** Estimator notes are short. A longer document is a quote, a report or an RFQ. */
export const ESTIMATOR_NOTES_MAX_CHARS = 4000;

export interface EstimatorPricedNotesResult {
    isEstimatorNotes: boolean;
    /** Lines that read as the estimator's own price, rate or sum. */
    priceLines: number;
    evidence: string[];
}

// ─── Another business, a letterhead, a formal quote ────────────────────────
// These guards were the content half of the D6 estimator-notes guard in
// document-type-classifier.ts (rounds 4 to 6). They moved here unchanged.

// Anything that names a business: an ABN or ACN, a legal suffix, a licence,
// an email, a web address, a contact line or an Australian phone number.
const OTHER_BUSINESS_IDENTITY_RES: RegExp[] = [
    /\b(?:ABN|ACN)(?![a-z])/i,
    /\bPty\.?\s*Ltd\b|\bLtd\b|\bLimited\b|\bP\s*[/-]\s*L\b|\bPty\b/i,
    /\b(?:licen[cs]e|lic)\.?\s*(?:no|number|#)|\b(?:licen[cs]e|lic)\b\.?\s*[:#]?\s*[a-z]{0,3}\d{3,}|\bbuilder'?s\s+licen[cs]e\b|\bcontractor\s+(?:licen[cs]e|lic)\b/i,
    /\bquote\s+from\b|\btrading\s+as\b|\bt\/as?\b/i,
    /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/,
    /\bwww\.|https?:\/\/|\.com(?:\.au)?\b|\.net(?:\.au)?\b/i,
    /(?:^|\n)\s*(?:ph|phone|tel|mob|mobile|fax)\b\s*[:.]|(?:^|\n|\s)(?:ph|phone|tel|mob|mobile|fax)\b\.?\s*[:#]?\s*[(+]?\d/i,
    /(?<!\d)(?:\+?61[\s-]?|0)[2-478](?:[\s-]?\d){8}(?!\d)/,
    /\(\s*0[2-478]\s*\)\s*\d{4}[\s-]?\d{4}(?!\d)/,
    /(?<!\d)1[38]00(?:[\s-]?\d){6}(?!\d)/,
];

// A trade business named in a heading line ("SMITH PLUMBING SERVICES").
const TRADE_BUSINESS_RE = /([a-z][a-z'&.]*)\s+(?:roofing|waterproofing|plumbing|electrical|electrics|painting|painters|builders|building|builds|constructions?|carpentry|joinery|tiling|tilers|glazing|scaffolding|services|group|contracting|contractors|maintenance|projects|solutions|enterprises|industries|trades|plastering|concreting|rendering|remedial|restorations?|developments|holdings|partners|sons|bros|brothers|homes|housing|renovations|improvements|civil|demolition|excavations|fencing|landscaping|flooring|cabinetry|coatings|engineering|engineers|consulting|consultants|fabrications?|installations)\b/gi;

// Street types: the word before one is a street name, not a person.
const STREET_TYPE_WORDS = [
    'st', 'street', 'rd', 'road', 'ave', 'avenue', 'pde', 'parade', 'cres', 'crescent', 'dr', 'drive', 'pl',
    'place', 'ln', 'lane', 'hwy', 'highway', 'tce', 'terrace', 'cct', 'circuit', 'ct', 'court', 'blvd',
    'boulevard', 'way', 'cl', 'close', 'gr', 'grove', 'esp', 'esplanade', 'sq', 'square',
];

// Words a heading line may hold without being a letterhead: the job, the
// element, the place, the trade and the state of the notes.
const HEADING_JOB_WORDS: ReadonlySet<string> = new Set([
    ...STREET_TYPE_WORDS,
    'quote', 'quotes', 'quoting', 'estimate', 'estimates', 'estimator', 'estimating', 'pricing', 'take', 'off',
    'takeoff', 'notes', 'note',
    'balcony', 'balconies', 'roof', 'roofs', 'bathroom', 'bathrooms', 'ensuite', 'laundry', 'kitchen', 'wall',
    'walls', 'window', 'windows', 'facade', 'external', 'internal', 'basement', 'carpark', 'podium', 'planter',
    'planters', 'deck', 'stair', 'stairs', 'stairwell', 'shower', 'slab', 'gutter', 'gutters', 'unit',
    'units', 'level', 'lobby', 'pool', 'driveway', 'fence', 'ceiling', 'floor', 'concrete', 'membrane', 'leak',
    'and', 'main', 'rear', 'front', 'north', 'south', 'east', 'west', 'whole', 'new', 'old', 'common', 'site',
    'job', 'garage', 'courtyard', 'entry', 'lift', 'plant', 'room', 'awning', 'canopy', 'parapet', 'box',
    'downpipe', 'downpipes', 'skylight', 'door', 'doors', 'remedial', 'repair', 'repairs', 'general', 'minor',
    'urgent', 'extra', 'additional', 'variation', 'for', 'the', 'of', 'to', 'at', 'on', 'in', 're', 'my', 'our',
    'final', 'draft', 'visit', 'inspection', 'revised', 'initial', 'original', 'rough', 'strata', 'client',
    'owner', 'owners', 'project', 'scope', 'works', 'block', 'building', 'hallway', 'corridor',
    'foyer', 'verandah', 'veranda', 'patio', 'porch', 'carport', 'shed', 'pergola', 'retaining', 'gate', 'gates',
    'path', 'pathway', 'garden', 'yard', 'eaves', 'fascia', 'soffit', 'chimney', 'tower', 'elevation', 'upper',
    'lower', 'ground', 'top', 'bottom', 'left', 'right', 'side', 'back', 'all', 'both', 'other', 'option',
    'roofing', 'waterproofing', 'plumbing', 'electrical', 'painting', 'tiling', 'rendering', 'plastering',
    'concreting', 'carpentry', 'joinery', 'glazing', 'scaffolding', 'fencing', 'landscaping', 'flooring',
    'demolition', 'spalling', 'cracking', 'leaks', 'defects', 'maintenance', 'restoration', 'renovation',
    'strip', 'remove', 'replace', 'demo', 'demolish', 'patch', 'seal', 'reseal', 'prep', 'prime', 'render', 'paint',
    'tile', 'retile', 'area', 'areas', 'then', 'before', 'after', 'with', 'incl', 'including', 'full', 'partial',
    'complete', 'finish', 'stage', 'elevations', 'make', 'good', 'install', 'supply', 'existing',
]);
const HEADING_WORD_RE = /^(?:[A-Z0-9][A-Za-z0-9'&.-]*|&|-|–)$/;

// OCR text and pasted text join words with "_" or "-". "_" is a word
// character, so \b never fires inside "ABC_Roofing_Pty_Ltd".
function spaceWordJoins(text: string): string {
    return text.replace(/[_]+/g, ' ').replace(/(?<=[a-z0-9])-(?=[a-z])|(?<=[a-z])-(?=[a-z0-9])/gi, ' ');
}

/** The first mark of a business in the text (ABN, Pty Ltd, licence, email, web, phone), or null. */
export function otherBusinessIdentity(text: string): string | null {
    for (const candidate of [text, spaceWordJoins(text)]) {
        const identity = OTHER_BUSINESS_IDENTITY_RES.map(pattern => candidate.match(pattern)?.[0]).find(Boolean);
        if (identity) return identity;
    }
    return null;
}

/**
 * A letterhead without a legal suffix, phone or email ("SMITH PLUMBING
 * SERVICES"). Only a heading-shaped line is read: short, no "$", every word
 * capitalised. Nothing is removed from the line first, not even the active
 * business's own name: a notes file headed with any business name is not
 * the business's working notes (review 6, critic C3).
 */
export function contentLetterheadBusiness(sample: string): string | null {
    for (const rawLine of sample.split('\n')) {
        const line = spaceWordJoins(rawLine).trim();
        if (!line || line.length > 60 || line.includes('$')) continue;
        const words = line.split(/\s+/);
        if (words.length > 6 || !words.every(word => HEADING_WORD_RE.test(word))) continue;
        TRADE_BUSINESS_RE.lastIndex = 0;
        for (const match of line.matchAll(TRADE_BUSINESS_RE)) {
            if (!HEADING_JOB_WORDS.has((match[1] || '').toLowerCase())) return match[0];
        }
    }
    return null;
}

// The layout of a formal quote. "Quote No" here never matches the start of
// "Quote notes".
const FORMAL_QUOTE_LAYOUT_RES: RegExp[] = [
    /\bquot(?:e|ation)\s*(?:#|no(?![a-z])\.?|number)/i,
    /\bprepared\s+for\b/i,
    /\bvalid(?:ity)?\b[^\n]{0,20}\b\d+\s*days?\b/i,
    /(?:^|\n)\s*scope\s+of\s+works?\s*:?\s*(?:\n|$)/i,
    /\bitem\b[^\n]{0,20}\bdescription\b[^\n]{0,30}\b(?:amount|price|total)\b/i,
    /\bbanking\s+details\b|\bbsb\b|\bpayment\s+terms\b|\bdeposit\s+(?:on\s+acceptance|required)\b|\bthank\s+you\s+for\s+the\s+opportunity\b|\bterms\s+(?:and|&)\s+conditions\b/i,
];

// The priced schedule of a client quote or tender, which keeps its shape
// when pasted without its letterhead ("Lump Sum Work Items", "Prices
// (Including GST)", "Sum Total Price including GST", "Refer to the image
// provided"). Added after the CN1 measurement read RJ Bird tender schedules
// pasted without a header.
const CLIENT_SCHEDULE_RES: RegExp[] = [
    /(?:^|\n)\s*(?:\d+(?:\.\d+)*\.?\s+)?lump\s+sum\s+(?:work\s+)?items?\b/i,
    /\bprices?\s*\(\s*(?:including|incl\.?|inc\.?|excluding|excl\.?|ex\.?)\s*gst\s*\)/i,
    /\bsum\s+total\b/i,
    /\btotal\s+(?:contract|tender)\s+(?:price|sum)\b|\bcontract\s+(?:sum|price)\b/i,
    /\bschedule\s+of\s+(?:prices|rates)\b/i,
    /\brefer\s+to\s+the\s+(?:images?|photos?|photographs?|drawings?|plans?)\s+(?:provided|attached)\b/i,
];

// ─── Documents that are not working notes, by what they say ────────────────

// The text asks for a price: an RFQ or a request email.
const REQUEST_ASK_RE = /\b(?:please\s+(?:can\s+you\s+)?(?:provide|quote|send|submit|supply|price|arrange|inspect)|kindly\s+(?:provide|quote|send|submit|supply)|(?:can|could|would)\s+you\s+(?:please\s+)?(?:provide|quote|price|send|arrange|inspect)|request\s+for\s+(?:a\s+)?(?:quot(?:e|ation)|pricing|proposal|tender)|(?:quot(?:e|ation)|pricing|tender)\s+request|invitation\s+to\s+(?:quot(?:e|ation)|tender)|seeking\s+(?:a\s+)?(?:quot(?:e|ation)|proposal)|(?:we|i)\s+(?:require|need|would\s+like)\s+(?:a\s+)?(?:quot(?:e|ation)|price|estimate))\b/i;

// An invoice or statement.
const INVOICE_RE = /\b(?:tax\s+invoice|invoice\s*(?:no\b\.?|number|#|date)|amount\s+(?:due|payable|owing)|balance\s+(?:due|owing)|due\s+date|payment\s+due|remittance|statement\s+of\s+account)\b/i;

// Words written to a reader: a greeting, an addressee or subject line, a
// sign-off, a "quotation", or the business speaking to its client. Working
// notes are not addressed to anyone.
const ADDRESSED_TO_READER_RE = /(?:^|\n)\s*(?:dear|hi|hello|good\s+(?:morning|afternoon))\b|(?:^|\n)\s*(?:re|subject|attention|attn|to|from|fwd?)\s*:|\bquotation\b|\b(?:kind\s+regards|best\s+regards|warm\s+regards|regards|yours\s+(?:sincerely|faithfully)|thank\s+you|thanks|cheers)\b|\bwe\s+(?:are\s+pleased|propose|offer)\b|\bplease\s+do\s+not\s+hesitate\b|\bour\s+(?:price|quote|quotation|offer|tender|fee)\b|\ball\s+works?\s+(?:is\s+|are\s+)?guaranteed\b/i;

// A report: a title line that says report, or two report section headings.
const REPORT_TITLE_RE = /\breport\b/i;
const REPORT_HEADING_RE = /^\s*(?:\d+(?:\.\d+)*\.?\s+)?(?:executive\s+summary|introduction|background|methodology|observations?|findings|recommendations?|conclusions?|limitations|disclaimer|purpose(?:\s+of\s+(?:the\s+)?(?:report|inspection))?|scope\s+of\s+(?:the\s+)?(?:inspection|report)|inspection\s+(?:details|findings|summary)|site\s+inspection|defects?\s+(?:observed|identified|summary)|photographs?|appendix(?:\s+[a-z0-9]+)?)\s*:?\s*$/i;

// ─── Price lines ───────────────────────────────────────────────────────────

// Money: a dollar figure, an AUD figure, or a figure with GST written after it.
const MONEY_RE = /(?:\$|\bA\$|\bAUD\b)\s*\d|\d[\d,.]*\s*(?:(?:inc|incl|ex|excl)\.?\s*gst|\+\s*gst)\b/i;
// Each money figure on a line, so the last one and what follows it can be read.
const MONEY_FIGURE_RE = /(?:\$|\bA\$|\bAUD\b)\s*\d(?:[\d,.]|\s(?=\d{3}\b))*|\d[\d,.]*\s*(?:(?:inc|incl|ex|excl)\.?\s*gst|\+\s*gst)\b/gi;
// What may follow a price at the end of a line: GST words, a unit or a rate,
// each, an "approx", brackets and punctuation (isPriceTail, below).
// A quantity times a rate: "18sqm x $40", "2 men x 3 days @ 650", "46sqm @ $85".
const QUANTITY_RATE_RE = /\d\s*[a-z²³/]*\s*(?:[x×*]|@)\s*\$?\s*\d|\$\s*\d[\d,.]*\s*(?:\/|per\s+)(?:sqm|m2|m²|lm|m|hr|hour|day|man|ea|each|unit|item|tonne|t|l|litre)\b/i;
// Words that make a money figure inside a line a price: a sum, an
// allowance, a cost word or a trade cost head.
const PRICE_CONTEXT_RE = /\b(?:total|totals|sub-?total|margin|mark-?up|profit|admin|overheads?|contingency|allow|allowance|labou?r|materials?|supply|plant|hire|cost|costs|price|rate|rates|gst)\b|=/i;
// The estimator's own sum or allowance: on its own it makes a short list of
// operations priced notes ("Total price = $2,750 incl gst", "allow $640").
const BUILDER_SUM_RE = /\b(?:total|sub-?total|grand\s+total|margin|mark-?up|profit|admin|overheads?|contingency|allow|allowance)\b|=\s*(?:\$|\bA\$|\bAUD\b)?\s*\d/i;
// Words that tell a figure in passing: someone else's price, a past repair,
// a claim, a budget. Such a line is not the estimator's own price.
const NARRATIVE_RE = /\b(?:said|says|advised|advise|mentioned|told|reported|according|paid|spent|previous(?:ly)?|last\s+(?:time|year|month|week)|ago|earlier|handyman|insurer|insurance|excess|claim|owner|owners|tenant|tenants|resident|residents|strata|committee|manager|budget|budgeted|invoice[ds]?|invoiced|was|were|had|has\s+been|have\s+been|quoted\s+by|quote\s+from|their\s+(?:quote|price)|another\s+builder|other\s+builder|competitor)\b/i;
// A line longer than this is prose, not a working-notes line.
const MAX_NOTES_LINE_CHARS = 160;

// A single sum line ("Total price = $2,750 incl gst") makes short notes
// priced notes only when the rest is a short list of operations.
const SINGLE_SUM_MAX_LINES = 30;
const SINGLE_SUM_MAX_AVG_LINE_CHARS = 90;
// Otherwise at least this share of the content lines are price lines.
const MIN_PRICE_LINE_SHARE = 0.25;

export interface PriceLineReading {
    priced: boolean;
    sum: boolean;
}

// ─── The text after a line's last price ────────────────────────────────────
// Read token by token (2026-10-29). It was one pattern,
//   /^\s*(?:k\b)?\s*(?:\(?\s*(?:TOKEN)\s*\)?\s*)*[.,;:)\]!?-]*\s*$/i
// whose whitespace before, inside and after the repeated group backtracked
// exponentially: " inc" sixteen times took 6.4 s, and the server copy, which
// reads long lines, blocked the event loop. This accepts exactly the same
// tails: optional spaces and a "k", then any number of tokens, each with an
// optional "(" before and ")" after and spaces around, then closing
// punctuation and spaces. Every place a token can end is tried once, so the
// work grows with the length of the tail, never with the ways to split it.
const TAIL_SPACE_RE = /\s/;
const TAIL_WORD_CHAR_RE = /[A-Za-z0-9_]/;
const TAIL_RUN_CHAR_RE = /[a-z0-9²³]/i;
const TAIL_CLOSING = new Set(['.', ',', ';', ':', ')', ']', '!', '?', '-']);
// The tokens with one spelling (the old pattern's gst, ea, each, p/?h, sqm, m2, m², lm, hrs?, days?, approx.?, appx.?,
// roughly, total, min(imum)?, max(imum)?, allowance and allow).
const TAIL_WORDS = [
    'gst', 'ea', 'each', 'ph', 'p/h', 'sqm', 'm2', 'm²', 'lm', 'hr', 'hrs', 'day', 'days', 'approx', 'approx.', 'appx', 'appx.',
    'roughly', 'total', 'min', 'minimum', 'max', 'maximum', 'allowance', 'allow',
];
const TAIL_GST_BASES = ['inc', 'incl', 'ex', 'excl'];

/** True when the text after a line's last price is only GST words, units, rates, brackets and punctuation. */
export function isPriceTail(tail: string): boolean {
    const text = String(tail);
    const n = text.length;
    const isSpace = (i: number) => i < n && TAIL_SPACE_RE.test(text.charAt(i));
    const skipSpaces = (from: number) => {
        let i = from;
        while (isSpace(i)) i += 1;
        return i;
    };
    // ASCII letters match either case, as the old /i pattern matched them.
    const wordAt = (i: number, word: string) => {
        if (i + word.length > n) return false;
        for (let k = 0; k < word.length; k += 1) {
            const c = text.charAt(i + k);
            const w = word.charAt(k);
            if (c !== w && c !== w.toUpperCase()) return false;
        }
        return true;
    };
    // Every place one token that starts at r can end.
    const tokenEnds = (r: number): number[] => {
        const ends: number[] = [];
        for (const base of TAIL_GST_BASES) {
            if (!wordAt(r, base)) continue;
            const after = r + base.length;
            for (const end of text[after] === '.' ? [after, after + 1] : [after]) {
                ends.push(end);
                const gst = skipSpaces(end);
                if (wordAt(gst, 'gst')) ends.push(gst + 3);
            }
        }
        if (text[r] === '+') {
            const gst = skipSpaces(r + 1);
            if (wordAt(gst, 'gst')) ends.push(gst + 3);
        }
        for (const word of TAIL_WORDS) if (wordAt(r, word)) ends.push(r + word.length);
        const run = (from: number) => {
            for (let i = from; i < n && TAIL_RUN_CHAR_RE.test(text.charAt(i)); i += 1) ends.push(i + 1);
        };
        if (wordAt(r, 'per') && isSpace(r + 3)) run(skipSpaces(r + 3));
        if (text[r] === '/') run(skipSpaces(r + 1));
        if (wordAt(r, 'all') && isSpace(r + 3)) {
            const up = skipSpaces(r + 3);
            if (wordAt(up, 'up')) ends.push(up + 2);
        }
        return ends;
    };
    // Where the tokens may start: after the leading spaces, and after a "k" that ends a word.
    const first = skipSpaces(0);
    const queue = [first];
    if ((text[first] === 'k' || text[first] === 'K') && !(first + 1 < n && TAIL_WORD_CHAR_RE.test(text.charAt(first + 1)))) {
        queue.push(skipSpaces(first + 1));
    }
    const reached = new Set<number>();
    while (queue.length) {
        const at = queue.pop() as number;
        if (reached.has(at)) continue;
        reached.add(at);
        for (const open of text[at] === '(' ? [at, at + 1] : [at]) {
            for (const end of tokenEnds(skipSpaces(open))) {
                const next = skipSpaces(end);
                queue.push(next);
                if (text[next] === ')') queue.push(skipSpaces(next + 1));
            }
        }
    }
    // From any place the tokens reached: closing punctuation, then spaces to the end.
    for (const at of reached) {
        let i = at;
        while (i < n && TAIL_CLOSING.has(text.charAt(i))) i += 1;
        if (skipSpaces(i) === n) return true;
    }
    return false;
}

/** One line's reading: the estimator's own price, rate or sum, and whether it is a sum. Exported for the server copy's test. */
export function readPriceLine(line: string): PriceLineReading {
    const none = { priced: false, sum: false };
    if (line.length > MAX_NOTES_LINE_CHARS) return none;
    const hasMoney = MONEY_RE.test(line);
    const quantityRate = QUANTITY_RATE_RE.test(line) && (hasMoney || /=\s*\$?\s*\d/.test(line));
    if (!hasMoney && !quantityRate) return none;
    if (NARRATIVE_RE.test(line)) return none;
    let last: RegExpMatchArray | null = null;
    for (const match of line.matchAll(MONEY_FIGURE_RE)) last = match;
    const trailing = !!last && isPriceTail(line.slice((last.index ?? 0) + last[0].length));
    if (!trailing && !quantityRate && !PRICE_CONTEXT_RE.test(line)) return none;
    return { priced: true, sum: BUILDER_SUM_RE.test(line) };
}

function contentLines(text: string): string[] {
    return text.replace(/\r\n?/g, '\n').split('\n').map(line => line.trim()).filter(line => /[A-Za-z0-9]/.test(line));
}

function notNotes(priceLines: number, evidence: string): EstimatorPricedNotesResult {
    return { isEstimatorNotes: false, priceLines, evidence: [evidence] };
}

/**
 * Whether a text is the builder's own priced working notes, read from the
 * text alone. Never reads a file name.
 */
export function detectEstimatorPricedNotes(text: unknown): EstimatorPricedNotesResult {
    const value = typeof text === 'string' ? text.trim() : '';
    if (!value) return notNotes(0, 'no text');
    if (value.length > ESTIMATOR_NOTES_MAX_CHARS) {
        return notNotes(0, `longer than ${ESTIMATOR_NOTES_MAX_CHARS} characters, which working notes are not`);
    }
    const lines = contentLines(value);
    const readings = lines.map(readPriceLine);
    const priceLines = readings.filter(reading => reading.priced).length;
    if (priceLines === 0) return notNotes(0, 'no line reads as the estimator\'s own price');

    const business = otherBusinessIdentity(value);
    if (business) return notNotes(priceLines, `it names a business ("${business.trim()}")`);
    const letterhead = contentLetterheadBusiness(value);
    if (letterhead) return notNotes(priceLines, `it has a business letterhead ("${letterhead.trim()}")`);
    const layout = FORMAL_QUOTE_LAYOUT_RES.map(pattern => value.match(pattern)?.[0]).find(Boolean);
    if (layout) return notNotes(priceLines, `it has a formal quote layout ("${layout.trim()}")`);
    const schedule = CLIENT_SCHEDULE_RES.map(pattern => value.match(pattern)?.[0]).find(Boolean);
    if (schedule) return notNotes(priceLines, `it has a client price schedule ("${schedule.trim()}")`);
    const ask = value.match(REQUEST_ASK_RE)?.[0];
    if (ask) return notNotes(priceLines, `it asks for a price ("${ask.trim()}")`);
    const invoice = value.match(INVOICE_RE)?.[0];
    if (invoice) return notNotes(priceLines, `it reads as an invoice ("${invoice.trim()}")`);
    const addressed = value.match(ADDRESSED_TO_READER_RE)?.[0];
    if (addressed) return notNotes(priceLines, `it is written to a reader ("${addressed.trim()}")`);
    const reportTitle = lines.slice(0, 3).find(line => line.length <= 80 && REPORT_TITLE_RE.test(line));
    if (reportTitle) return notNotes(priceLines, `it is titled as a report ("${reportTitle}")`);
    const reportHeadings = lines.filter(line => REPORT_HEADING_RE.test(line));
    if (reportHeadings.length >= 2) return notNotes(priceLines, `it has report sections ("${reportHeadings.slice(0, 2).join('", "')}")`);

    const share = priceLines / lines.length;
    if (priceLines >= 2 && share >= MIN_PRICE_LINE_SHARE) {
        return {
            isEstimatorNotes: true,
            priceLines,
            evidence: [`${priceLines} of ${lines.length} lines are the estimator's own prices, rates or sums`],
        };
    }
    const averageLine = lines.reduce((sum, line) => sum + line.length, 0) / lines.length;
    const sumLine = readings.some(reading => reading.priced && reading.sum);
    if (sumLine && lines.length <= SINGLE_SUM_MAX_LINES && averageLine <= SINGLE_SUM_MAX_AVG_LINE_CHARS) {
        return {
            isEstimatorNotes: true,
            priceLines,
            evidence: [`a short list of operations with the estimator's own sum (${priceLines} price line${priceLines === 1 ? '' : 's'} in ${lines.length} lines)`],
        };
    }
    return notNotes(priceLines, `only ${priceLines} of ${lines.length} lines carry a price, which is a figure in passing, not working notes`);
}
